import { create } from 'zustand';

interface SalesDetail {
  customerId: number;
  masterId: number;
  transactionDate: string;
  employeeId: number;
  customerName: string;
  regionId: number;
  regionName: string;
  employeeName: string;
  totalInvoice: number;
  isAreaRelated: boolean;
}

interface ReturnDetail {
  employeeId: number;
  regionId: number;
  regionName: string;
  clientName: string;
  returnInvoiceId: number;
  masterId: number;
  totalReturn: number;
  isAreaRelated: boolean;
}

interface Summary {
  totalSales: number;
  totalReturns: number;
  netSales: number;
  areaRelatedSales: number;
  areaRelatedReturns: number;
  areaRelatedNetSales: number;
  nonAreaRelatedSales: number;
  nonAreaRelatedReturns: number;
  nonAreaRelatedNetSales: number;
  transactionCount: number;
  returnCount: number;
  topCustomers: Array<{
    customerId: number;
    customerName: string;
    totalAmount: number;
    regionName: string;
    isAreaRelated: boolean;
  }>;
}

interface InvoiceDetailsData {
  employeeId: number;
  employeeName: string;
  monthYear: string;
  storeId: number;
  departmentType: 'retail' | 'wholesale';
  salesDetails: SalesDetail[];
  returnDetails: ReturnDetail[];
  summary: Summary;
}

interface EmployeeInvoiceDetailsStore {
  isOpen: boolean;
  data: InvoiceDetailsData | null;
  isLoading: boolean;
  error: string | null;
  activeTab: 'areaRelated' | 'nonAreaRelated';
  activeSubTab: 'sales' | 'returns';

  // Employee context
  employeeId: number | null;
  employeeName: string;
  storeId: 1 | 2 | null;
  monthYear: string;
  employeeMongoId: string | null;
  avatar: string | null;
  target: number;

  // Actions
  openDialog: (params: {
    employeeId: number;
    employeeName: string;
    storeId: 1 | 2;
    monthYear: string;
    employeeMongoId?: string | null;
    avatar?: string | null;
    target?: number;
  }) => void;
  closeDialog: () => void;
  setData: (data: InvoiceDetailsData | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setActiveTab: (tab: 'areaRelated' | 'nonAreaRelated') => void;
  setActiveSubTab: (tab: 'sales' | 'returns') => void;
  reset: () => void;
}

export const useEmployeeInvoiceDetailsStore =
  create<EmployeeInvoiceDetailsStore>((set) => ({
    isOpen: false,
    data: null,
    isLoading: false,
    error: null,
    activeTab: 'areaRelated',
    activeSubTab: 'sales',
    employeeId: null,
    employeeName: '',
    storeId: null,
    monthYear: '',
    employeeMongoId: null,
    avatar: null,
    target: 0,

    openDialog: (params) =>
      set({
        isOpen: true,
        employeeId: params.employeeId,
        employeeName: params.employeeName,
        storeId: params.storeId,
        monthYear: params.monthYear,
        employeeMongoId: params.employeeMongoId || null,
        avatar: params.avatar || null,
        target: params.target || 0,
      }),
    closeDialog: () => set({ isOpen: false }),
    setData: (data) => set({ data, error: null }),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error, isLoading: false }),
    setActiveTab: (activeTab) => set({ activeTab }),
    setActiveSubTab: (activeSubTab) => set({ activeSubTab }),
    reset: () =>
      set({
        isOpen: false,
        data: null,
        isLoading: false,
        error: null,
        activeTab: 'areaRelated',
        activeSubTab: 'sales',
        employeeId: null,
        employeeName: '',
        storeId: null,
        monthYear: '',
        employeeMongoId: null,
        avatar: null,
        target: 0,
      }),
  }));
